/** @module ProductPack */


import { uniqWith, zip } from 'ramda';

import {
  getPackItemReference,
  getPackItemFormulaId,
  setPackItemUsage,
  setPackItemPrice,
  setPackItemStatus,
  getPackItemCategories,
  getPackItemSize,
  getPackItemStatus,
  getPackItemPrice,
  getPackItemUsage,
  getPackItemName,
  hasPackItemIngredients,
  getPackItemIngredients,
  setPackItemStockQuantity,
  getPackItemStockQuantity,
  isPackItemEqual,
  getPackItemNameByReference,
  getPackItemUniqueId,
  getPackItemNameByUniqueId
} from '@/models/core/PackItem';
import { getSizeValue, Size } from '@/models/core/values';
import { isIngredientEqual } from '@/models/core/Ingredient';

import { PRODUCT_NAMES, SERUM_USAGES } from '@/constants/treatment';

import { COUNTRY_ISO } from '@/constants/countries';

/**
 * A pack of bottles (it may contain only one bottle).
 * This pack could be sold as a single unit.
 *
 * @typedef {Object} ProductPack
 * @property {PackItem[]} items
 */

/**
 * ProductPack constructor.
 *
 * @function
 * @param {PackItem[]} items - E.g. serum bottles (formulas)
 * @return {ProductPack}
 */
export const ProductPack = items => {
  return { items };
};

/**
 * Get name of productPack.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string}
 */
export const getProductPackName = productPack => {
  const [firstItem] = productPack.items;

  return getPackItemName(firstItem);
};

/**
 * Get unique reference for product.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string}
 */
export const getProductPackReference = productPack => {
  const [firstItem] = productPack.items;

  return getPackItemReference(firstItem);
};

/**
 * Get formula ids from product pack.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string[]}
 */
export const getProductPackFormulaIds = productPack => {
  return productPack.items.map(getPackItemFormulaId).filter(id => id !== null);
};

/**
 * Set price on product.
 *
 * @function
 * @param {Price} price
 * @param {ProductPack} productPack
 * @return {ProductPack}
 */
export const setProductPackPrice = (price, productPack) => {
  const { items } = productPack;
  const processedItems = items.map(item => setPackItemPrice(price, item));

  return ProductPack(processedItems);
};

/**
 * Set usage on product.
 *
 * @function
 * @param {string} usage
 * @param {ProductPack} productPack
 * @return {ProductPack}
 */
export const setProductPackUsage = (usage, productPack) => {
  const { items } = productPack;
  const processedItems = items.map(item => setPackItemUsage(usage, item));

  return ProductPack(processedItems);
};

/**
 * Set status on product.
 *
 * @function
 * @param {string} status
 * @param {ProductPack} productPack
 * @return {ProductPack}
 */
export const setProductPackStatus = (status, productPack) => {
  const { items } = productPack;
  const processedItems = items.map(item => setPackItemStatus(status, item));

  return ProductPack(processedItems);
};

/**
 * Get product categories.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string[]}
 */
export const getProductPackCategories = productPack => {
  const [firstItem] = productPack.items;

  return getPackItemCategories(firstItem);
};

/**
 * Check is product a serum.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isProductPackSerum = productPack => {
  return [PRODUCT_NAMES.FACE_SERUM, PRODUCT_NAMES.EYE_CONTOUR_SERUM].includes(
    getProductPackName(productPack)
  );
};

/**
 * Check if product pack contains two bottles with the same formula (double serum).
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isProductPackWithDuplicatedBottles = productPack => {
  const { items } = productPack;

  return items.length === 2 && isPackItemEqual(items[0], items[1]);
};

/**
 * Get product pack size.
 *
 * Notice, that size units may differ from the units of individual items.
 * Because there might be more than one item in a pack. And
 * we keep the size value (the number) only for pack's item,
 * not for the whole pack (e.g. if there are two items
 * in a pack, each one of 15 ml size, then we keep the value
 * 15 for the pack). But we may use different units for
 * the pack with multiple items (e.g. '15 ml x 2' for face serum pack with two bottles).
 *
 * @function
 * @param {ProductPack} productPack
 * @return {Size}
 */
export const getProductPackSize = productPack => {
  const { items } = productPack;
  const [firstItem] = items;
  // alert(JSON.stringify(firstItem));
  const size = getPackItemSize(firstItem);
  // alert(JSON.stringify(size));
  if (isProductPackSerum(productPack) && items.length > 1) {
    // TODO: consider using standard units
    return Size(getSizeValue(size), 'ml x 2');
  }

  return size;
};

/**
 * Check is productPack a generic formula eye serum pack.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isProductPackGenericEyeSerum = productPack => {
  const name = getProductPackName(productPack);
  const formulaIds = getProductPackFormulaIds(productPack);

  // for now only generic eye formula has formulas with ids (e.g. face serum formulas do not have ids)
  return name === 'eyeContourSerum' && formulaIds.length > 0;
};

/**
 * Get formula id of generic eye serum.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string}
 */
export const getProductPackGenericEyeSerumFormulaId = productPack => {
  if (!isProductPackGenericEyeSerum(productPack)) {
    throw Error('Invalid argument. Requires generic eye serum product pack as an argument');
  }
  const [firstItem] = productPack.items;

  return getPackItemFormulaId(firstItem);
};

/**
 * Check if product available.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isProductPackAvailable = productPack => {
  const [firstItem] = productPack.items;
  const status = getPackItemStatus(firstItem);

  return status !== 'not-available';
};

/**
 * Check if product in stock.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isProductPackInStock = productPack => {
  const [firstItem] = productPack.items;
  const status = getPackItemStatus(firstItem);

  return status !== 'out-of-stock';
};

/**
 * Check if serum product is available to sell in a country.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const isRestrictedSaleInCountry = (productPack,country) => {
    const { items } = productPack;
    let isDMAEInside = false;
    if(!country) return false;
    if ( isProductPackSerum(productPack) && (country.toLowerCase() === COUNTRY_ISO.AUSTRALIA.toLowerCase())) {
      const ingredients = items.flatMap(getPackItemIngredients);   
      // alert(JSON.stringify(ingredients,null,4));  
      for (let i = 0; i < ingredients.length; i+=1 ) {
      if ((ingredients[i].name != null) && (ingredients[i].name === 'dmae')) {
          isDMAEInside = true;
          break;
        }
      }
    }
  return isDMAEInside;
};

/**
 * Get product price.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {Price}
 */
export const getProductPackPrice = productPack => {
  const [firstItem] = productPack.items;

  return getPackItemPrice(firstItem);
};

/**
 * Get items from product pack.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {PackItem[]}
 */
export const getProductPackItems = productPack => productPack.items;

/**
 * Get pack usage.
 *
 * Notice, that pack usage may differ from individual item usage,
 * because there might be multiple items in pack, each one with different usage.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string}
 */
export const getProductPackUsage = productPack => {
  const usages = productPack.items.map(getPackItemUsage);
  const differentUsages = Array.from(new Set(usages));

  if (differentUsages.length > 1) {
    return SERUM_USAGES.ONE_MORNING_ONE_EVENING;
  }

  return differentUsages[0];
};

/**
 * Check if a pack contains any serum ingredients.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {boolean}
 */
export const hasProductPackIngredients = productPack => {
  return productPack.items.some(hasPackItemIngredients);
};

/**
 * Return ingredients from all items concatenated (skip equal ingredients).
 *
 * @function
 * @param {ProductPack} productPack
 * @return {Ingredient[]}
 */
export const getProductPackAllIngredients = productPack => {
  const { items } = productPack;
  const ingredients = items.flatMap(getPackItemIngredients);

  return uniqWith(isIngredientEqual)(ingredients);
};

export const getProductPackStockQuantity = productPack => {
  const [firstItem] = productPack.items;

  return getPackItemStockQuantity(firstItem);
};

export const setProductPackStockQuantity = (stockQuantity, productPack) => {
  const { items } = productPack;
  const itemsProcessed = items.map(item => setPackItemStockQuantity(stockQuantity, item));

  return ProductPack(itemsProcessed);
};

/**
 * Check for equality two product packs.
 *
 * @function
 * @param {ProductPack} productPackA
 * @param {ProductPack} productPackB
 * @return {boolean}
 */
export const isProductPackEqual = (productPackA, productPackB) => {
  const itemsA = productPackA.items;
  const itemsB = productPackB.items;

  if (itemsA.length !== itemsB.length) {
    return false;
  }

  return zip(itemsA, itemsB).every(([itemA, itemB]) => isPackItemEqual(itemA, itemB));
};

export const getProductPackNameByReference = reference => getPackItemNameByReference(reference);

export const getProductPackNameByUniqueId = id => getPackItemNameByUniqueId(id);

/**
 * Get uniq id for product pack.
 *
 * @function
 * @param {ProductPack} productPack
 * @return {string}
 */
export const getProductPackUniqueId = productPack => {
  return productPack.items.map(getPackItemUniqueId).join('-');
};
